/**
 * The chrome: header, content column, footer.
 *
 * ── Why there is no rail ────────────────────────────────────────────────────
 *
 * `downwind-console` has a collapsible sidebar because an operator lives in it
 * for an afternoon and moves between seven screens on a ticket. A customer
 * visits this maybe four times a year, for one of three reasons, and a
 * persistent rail on four items is furniture that costs horizontal space on the
 * screens that actually matter — the license detail, which is a key and a
 * device list.
 *
 * Same deck, different posture: the console is dense because density is the
 * feature; this is the same forms and the same tokens with more air, because
 * the reader is not trained on it.
 */

import { Brand } from "@/components/DownwindMark";
import { Tag } from "@/components/deck";
import { portalApi } from "@/lib/api";
import { HEADER_NAV } from "@/lib/nav";
import { useSession } from "@/lib/session";
import { type ThemeMode, useTheme } from "@/lib/theme";
import { useToast } from "@/lib/toast";
import { cn } from "@/lib/utils";
import { LogOut, Monitor, Moon, Sun, UserRound } from "lucide-react";
import { useState } from "react";
import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router";

declare const __APP_VERSION__: string;

export function PortalShell() {
  return (
    <div className="flex min-h-screen flex-col bg-paper text-ink">
      <a
        href="#main"
        className="absolute left-2 top-2 z-50 -translate-y-20 rounded-[2px] bg-ink px-4 py-2 text-[0.875rem] font-medium text-paper focus:translate-y-0"
      >
        Skip to content
      </a>

      <PortalHeader />

      <main id="main" className="flex-1 px-4 py-8 lg:px-6 lg:py-12">
        <div className="mx-auto w-full max-w-[64rem]">
          <Outlet />
        </div>
      </main>

      <PortalFooter />
    </div>
  );
}

function PortalHeader() {
  const { state } = useSession();
  const signedIn = state?.signed_in === true;

  return (
    <header className="sticky top-0 z-20 border-b border-rule bg-sunk">
      <div className="mx-auto flex h-[var(--header-h)] w-full max-w-[64rem] items-center justify-between gap-4 px-4 lg:px-6">
        <div className="flex min-w-0 items-center gap-6">
          <Link
            to="/"
            aria-label="Downwind Studio — your licenses"
            className="shrink-0 rounded-[2px] transition-opacity hover:opacity-80"
          >
            <Brand />
          </Link>

          {/* The nav only exists once there is somewhere to go. A signed-out
              visitor has exactly one action available and it is on the page. */}
          {signedIn && (
            <nav aria-label="Sections" className="hidden items-center gap-1 sm:flex">
              {HEADER_NAV.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.to === "/"}
                  className={({ isActive }) =>
                    cn(
                      "rounded-[2px] px-2.5 py-1.5 text-[0.875rem] transition-colors",
                      isActive ? "bg-card text-ink" : "text-ink-2 hover:bg-card/60 hover:text-ink",
                    )
                  }
                >
                  {item.label}
                </NavLink>
              ))}
            </nav>
          )}
        </div>

        <div className="flex shrink-0 items-center gap-1.5">
          <ThemeToggle />
          {signedIn ? <AccountControl email={state.email} /> : null}
        </div>
      </div>

      {/* Small screens get the nav as a second row rather than a hamburger.
          Four items fit; a drawer for four items is a tap nobody needs. */}
      {signedIn && (
        <nav
          aria-label="Sections"
          className="flex items-center gap-1 overflow-x-auto border-t border-rule px-4 py-1.5 sm:hidden"
        >
          {HEADER_NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === "/"}
              className={({ isActive }) =>
                cn(
                  "shrink-0 rounded-[2px] px-2.5 py-1 text-[0.8125rem] transition-colors",
                  isActive ? "bg-card text-ink" : "text-ink-2",
                )
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>
      )}
    </header>
  );
}

/**
 * The identity control: who you are, and the two things you can do about it.
 *
 * A plain disclosure rather than a Radix menu — this is two links and it does
 * not need focus-trapping, portalling or a 12kB primitive. It closes on blur
 * and on navigation, which is the whole of the behaviour anyone expects.
 */
function AccountControl({ email }: { email: string }) {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { setState } = useSession();
  const { pushToast } = useToast();

  async function signOut() {
    setOpen(false);
    try {
      const next = await portalApi.signOut();
      setState(next);
      navigate("/signin", { replace: true });
    } catch {
      // The cookie is cleared by the Worker on any answer it gives, so the
      // useful recovery is a reload rather than a retry button.
      pushToast({
        kind: "error",
        title: "We could not sign you out cleanly.",
        body: "Reload the page and try again.",
      });
    }
  }

  return (
    <div
      className="relative"
      onBlur={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setOpen(false);
      }}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-haspopup="menu"
        className={cn(
          "flex h-8 items-center gap-2 rounded-[2px] border border-transparent px-2 text-[0.8125rem] text-ink-2 transition-colors",
          "hover:bg-card hover:text-ink",
          open && "border-rule-strong bg-card text-ink",
        )}
      >
        <UserRound className="size-4 shrink-0" />
        <span className="hidden max-w-[22ch] truncate md:inline">{email}</span>
      </button>

      {open && (
        <div
          role="menu"
          className="strip absolute right-0 top-full z-30 mt-1.5 w-56 p-1 shadow-[var(--shadow-raise)]"
        >
          <span aria-hidden="true" className="strip-bar bg-rule-strong" />
          <div className="border-b border-rule px-2.5 py-2">
            <div className="t-field">Signed in as</div>
            <div className="t-data mt-1 truncate text-[0.8125rem] text-ink" title={email}>
              {email}
            </div>
          </div>
          <Link
            to="/account"
            role="menuitem"
            onClick={() => setOpen(false)}
            state={{ from: location.pathname }}
            className="block rounded-[2px] px-2.5 py-2 text-[0.875rem] text-ink-2 transition-colors hover:bg-sunk hover:text-ink"
          >
            Account and devices
          </Link>
          <button
            type="button"
            role="menuitem"
            onClick={signOut}
            className="flex w-full items-center gap-2 rounded-[2px] px-2.5 py-2 text-left text-[0.875rem] text-ink-2 transition-colors hover:bg-sunk hover:text-ink"
          >
            <LogOut className="size-3.5" />
            Sign out
          </button>
        </div>
      )}
    </div>
  );
}

const NEXT_MODE: Readonly<Record<ThemeMode, ThemeMode>> = {
  system: "light",
  light: "dark",
  dark: "system",
};

const MODE_ICON: Readonly<Record<ThemeMode, typeof Sun>> = {
  system: Monitor,
  light: Sun,
  dark: Moon,
};

function ThemeToggle() {
  const { mode, setMode } = useTheme();
  const Icon = MODE_ICON[mode];
  return (
    <button
      type="button"
      onClick={() => setMode(NEXT_MODE[mode])}
      aria-label={`Theme: ${mode}. Switch to ${NEXT_MODE[mode]}.`}
      title={`Theme: ${mode}`}
      className="flex size-8 items-center justify-center rounded-[2px] text-ink-2 transition-colors hover:bg-card hover:text-ink"
    >
      <Icon className="size-4" />
    </button>
  );
}

/**
 * The footer earns its place with one thing: the build and environment a
 * customer's screenshot was taken on. Everything else here is a link they might
 * actually want from an account page.
 */
function PortalFooter() {
  return (
    <footer className="border-t border-rule px-4 py-6 lg:px-6">
      <div className="mx-auto flex w-full max-w-[64rem] flex-wrap items-center justify-between gap-x-6 gap-y-3">
        <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
          <a
            href="https://downwind.studio"
            className="text-[0.8125rem] text-ink-2 transition-colors hover:text-ink"
          >
            downwind.studio
          </a>
          <a
            href="https://aloftwx.com"
            className="text-[0.8125rem] text-ink-2 transition-colors hover:text-ink"
          >
            AloftWX
          </a>
          <Link
            to="/support"
            className="text-[0.8125rem] text-ink-2 transition-colors hover:text-ink"
          >
            Get help
          </Link>
        </div>
        <Tag dot={false} className="shrink-0">
          BUILD {__APP_VERSION__}
        </Tag>
      </div>
    </footer>
  );
}
